import { buildDemoAwareUrl, getDemoUserEmail, isDemoUserEnvironment } from '../utils/demoUser'

/**
 * SignalR Configuration
 * 
 * Environment variables:
 * - REACT_APP_SIGNALR_HUB_URL: SignalR hub URL (REQUIRED for development)
 *   Example for development: http://localhost:7118/api/hubs/codeitems
 *   Example for production: /api/hubs/codeitems (relative URL)
 * 
 * - REACT_APP_API_BASE_URL: If you have this set, SignalR will use it as base
 * 
 * - REACT_APP_SIGNALR_RETRY_DELAY_1: First retry delay in ms (default: 0)
 * - REACT_APP_SIGNALR_RETRY_DELAY_2: Second retry delay in ms (default: 2000)
 * - REACT_APP_SIGNALR_RETRY_DELAY_3: Third retry delay in ms (default: 10000)
 * - REACT_APP_SIGNALR_RETRY_DELAY_DEFAULT: Default retry delay in ms (default: 30000)
 * - REACT_APP_SIGNALR_CONNECTION_TIMEOUT: Connection timeout in ms (default: 30000)
 */

const getBaseHubUrl = (): string => {
  // Log all relevant environment variables for debugging
  

  let hubUrl: string

  // 1. Check if explicit SignalR URL is set
  if (process.env.REACT_APP_SIGNALR_HUB_URL) {
    hubUrl = process.env.REACT_APP_SIGNALR_HUB_URL
    
    return hubUrl
  }
  
  // 2. Try to construct from API base URL (if you have it in your .env)
  if (process.env.REACT_APP_API_BASE_URL) {
    // Extract base URL without /api suffix if present
    const baseUrl = process.env.REACT_APP_API_BASE_URL.replace(/\/api\/?$/, '').replace(/\/$/, '')
    hubUrl = `${baseUrl}/hubs/codeitems`
    console.log('[SignalR Config] Constructed from REACT_APP_API_BASE_URL:', hubUrl)
    return hubUrl
  }
  
  // 3. Default to relative URL (for production) - NO /api prefix
  // Backend maps hub at /hubs/codeitems (not /api/hubs/codeitems)
  hubUrl = '/hubs/codeitems'
  console.log('[SignalR Config] Using default relative URL:', hubUrl)
  return hubUrl
}

export const getSignalrHubUrl = (): string => {
  const baseHubUrl = getBaseHubUrl()
  return buildDemoAwareUrl(baseHubUrl, {
    demoUserEmail: getDemoUserEmail(),
    enabled: isDemoUserEnvironment(),
  })
}

export const signalrConfig = {
  get hubUrl() {
    return getSignalrHubUrl()
  },
  
  retryDelays: {
    first: parseInt(process.env.REACT_APP_SIGNALR_RETRY_DELAY_1 || '0', 10),
    second: parseInt(process.env.REACT_APP_SIGNALR_RETRY_DELAY_2 || '2000', 10),
    third: parseInt(process.env.REACT_APP_SIGNALR_RETRY_DELAY_3 || '10000', 10),
    default: parseInt(process.env.REACT_APP_SIGNALR_RETRY_DELAY_DEFAULT || '30000', 10),
  },
  
  connectionTimeout: parseInt(process.env.REACT_APP_SIGNALR_CONNECTION_TIMEOUT || '30000', 10),
  
  // Hub methods - these match CodeItemsHub.cs
  methods: {
    joinFormat: 'JoinFormat',
    leaveFormat: 'LeaveFormat',
  },
  
  // Hub events - these match CodeItemsHub.cs
  events: {
    usersInFormatUpdated: 'UsersInFormatUpdated',
    codeSheetMainMerged: 'CodeSheetMainMerged',
  },
} as const


export type SignalRConfig = typeof signalrConfig

